<template>
  <div class="batch-metadata-form atb-p-6">
    <h2 class="atb-text-2xl atb-font-bold atb-mb-6">
      {{ $t('uploadBatchMetadata') }}
    </h2>

    <form
      @submit.prevent="handleSubmit"
      class="atb-space-y-6"
      novalidate
    >
      <!-- Mandatory Fields Section -->
      <div class="atb-bg-gray-50 atb-p-4 atb-rounded">
        <h3 class="atb-text-lg atb-font-semibold atb-mb-4">
          {{ $t('requiredFields') }}
        </h3>

        <div class="atb-grid atb-grid-cols-1 atb-gap-4">
          <div>
            <label class="atb-block atb-text-sm atb-font-medium atb-mb-1">
              {{ $t('deploymentFile') }} *
            </label>
            <div class="atb-flex atb-items-center atb-gap-2">
              <input
                ref="fileInput"
                type="file"
                accept=".zip"
                @change="handleFileChange"
                class="atb-block atb-w-full atb-text-sm atb-text-gray-500 file:atb-mr-4 file:atb-py-2 file:atb-px-4 file:atb-rounded file:atb-border-0 file:atb-text-sm file:atb-font-semibold file:atb-bg-blue-50 file:atb-text-blue-700 hover:file:atb-bg-blue-100"
              />
            </div>
            <p
              v-if="selectedFile"
              class="atb-text-sm atb-text-gray-600 atb-mt-1"
            >
              {{ $t('selectedFile') }}: {{ selectedFile.name }} ({{
                formatFileSize(selectedFile.size)
              }})
            </p>
            <p
              v-if="fileError"
              class="atb-text-sm atb-text-red-600 atb-mt-1"
            >
              {{ fileError }}
            </p>
          </div>

          <div>
            <label class="atb-block atb-text-sm atb-font-medium atb-mb-1">
              {{ $t('systemName') }} *
            </label>
            <Textbox
              v-model="formData.systemName"
              placeholder=""
            />
          </div>

          <div>
            <label class="atb-block atb-text-sm atb-font-medium atb-mb-1">
              {{ $t('dataSource') }}
            </label>
            <Select
              v-model="formData.dataSource"
              :options="dataSourceOptions"
              label="name"
              value="name"
              :placeholder="$t('selectDatasourcePlaceholder')"
            />
          </div>

          <div>
            <div class="atb-flex atb-items-center atb-justify-between atb-mb-1">
              <span class="atb-block atb-text-sm atb-font-medium">
                {{ $t('inputData') }}
              </span>
              <Button
                id="batchInputManifestRefreshButton"
                motif="tertiary"
                type="button"
                :title="$t('refreshInputManifest')"
                aria-label="refresh input manifest button"
                :disabled="isRefreshingManifest"
                @click="refreshInputManifest"
              >
                <template #icon>
                  <span class="material-icons">loop</span>
                </template>
              </Button>
            </div>
            <p
              v-if="!formData.dataSource"
              class="atb-text-sm atb-text-gray-500 atb-italic"
            >
              {{ $t('selectDatasourceFirst') }}
            </p>
            <p
              v-else-if="manifestFilesForDatasource.length === 0"
              class="atb-text-sm atb-text-gray-500 atb-italic"
            >
              {{ $t('noInputFilesForDatasource') }}
            </p>
            <div
              v-else
              class="atb-border atb-border-solid atb-border-gray-300 atb-rounded atb-bg-white"
            >
              <AgGridVue
                id="batchInputFilesGrid"
                :style="{ height: '260px' }"
                class="ag-theme-eds atb-w-full"
                :row-data="inputFileRows"
                :column-defs="inputFileColumnDefs"
                row-selection="single"
                :suppress-cell-selection="true"
                @grid-ready="onInputGridReady"
                @selection-changed="onInputSelectionChanged"
              />
            </div>
            <p
              v-if="formData.s3InputKey"
              class="atb-text-sm atb-text-gray-600 atb-mt-1"
            >
              {{ $t('selectedInputFile') }}: {{ selectedInputFileName }}
            </p>
          </div>
        </div>
      </div>

      <!-- Form Actions -->
      <div class="atb-flex atb-gap-4 atb-justify-end">
        <Button
          motif="secondary"
          type="button"
          @click="handleCancel"
        >
          {{ $t('cancel') }}
        </Button>
        <Button
          motif="primary"
          type="submit"
          :disabled="isSubmitting || !selectedFile"
        >
          {{ isSubmitting ? $t('uploading') : $t('uploadToS3') }}
        </Button>
      </div>
    </form>
  </div>
  <StreamingProgressModal
    :show="showProgressModal"
    :header-text="$t('uploadingBatchMetadata')"
    :message="progressMessage"
    :default-message="$t('batchUploadProgressMessage')"
    :percent="progressPercent"
    :is-closable="false"
    @on-modal-close="showProgressModal = false"
  />
</template>

<script lang="ts" setup>
  import { ref, reactive, onMounted, computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Textbox from '@atb-ui/eds-vue/src/components/Textbox.vue';
  import Select from '@atb-ui/eds-vue/src/components/Select.vue';
  import StreamingProgressModal from '@/app/shared/components/StreamingProgressModal.vue';
  import {
    GridApi,
    GridReadyEvent,
    SelectionChangedEvent,
  } from 'ag-grid-community';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import { useBatchStore } from '@/app/features/admin/batch/batch-store';
  import { useClientStore } from '@/app/features/admin/clients/client-store';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { BatchMetadata } from '@/app/shared/types/models/batch-metadata.interface';
  import type { BatchMetadataProgressEvent } from '@/app/features/admin/batch/batch-service';

  interface BatchFormEvents {
    (e: 'cancel'): void;
    (e: 'success'): void;
  }

  interface InputFileRow {
    name: string;
    key: string;
  }

  const emit = defineEmits<BatchFormEvents>();
  const { t } = useI18n();
  const batchStore = useBatchStore();
  const clientStore = useClientStore();
  const alertStore = useAlertStore();
  const isSubmitting = ref(false);
  const isRefreshingManifest = ref(false);
  const selectedFile = ref<File | null>(null);
  const fileError = ref<string>('');
  const fileInput = ref<HTMLInputElement | null>(null);
  const inputGridApi = ref<GridApi | null>(null);

  const showProgressModal = ref(false);
  const progressMessage = ref('');
  const progressPercent = ref(0);

  const formData = reactive<BatchMetadata>({
    systemName: '',
    dataSource: '',
    s3InputKey: '',
  });

  const dataSourceOptions = computed(() => {
    return clientStore.dataSourceList.map((ds) => ({
      name: ds.name,
      value: ds.name,
    }));
  });

  const manifestFilesForDatasource = computed<string[]>(() => {
    const manifest = batchStore.inputManifest;
    if (!manifest || !formData.dataSource) return [];
    return manifest.filesByDataSource?.[formData.dataSource] ?? [];
  });

  const inputFileRows = computed<InputFileRow[]>(() => {
    return manifestFilesForDatasource.value.map((key) => ({
      name: key.split('/').pop() || key,
      key,
    }));
  });

  const inputFileColumnDefs = [
    {
      field: 'name',
      headerName: 'File Name',
      suppressMovable: true,
      sortable: true,
      flex: 1,
      minWidth: 200,
      checkboxSelection: true,
    },
  ];

  const selectedInputFileName = computed(() => {
    if (!formData.s3InputKey) return '';
    return formData.s3InputKey.split('/').pop() || formData.s3InputKey;
  });

  onMounted(async () => {
    try {
      await Promise.all([
        clientStore.getDataSourceList(),
        batchStore.fetchInputManifest(),
      ]);
    } catch (error) {
      console.error('Failed to load form data:', error);
    }
  });

  // When the datasource changes, reset any previously chosen input file so the
  // selection stays consistent with the rows now shown in the table.
  watch(
    () => formData.dataSource,
    () => {
      formData.s3InputKey = '';
      inputGridApi.value?.deselectAll();
    }
  );

  const onInputGridReady = (params: GridReadyEvent) => {
    inputGridApi.value = params.api;
    params.api.sizeColumnsToFit();
  };

  /**
   * Re-fetch the manifest from the backend and silently reconcile the current
   * selection against what the refreshed manifest contains. If the chosen
   * datasource was removed, both the datasource and input file selections are
   * cleared; if only the input file was removed, the datasource stays.
   */
  const refreshInputManifest = async () => {
    if (isRefreshingManifest.value) return;
    isRefreshingManifest.value = true;
    try {
      await batchStore.fetchInputManifest();

      const manifest = batchStore.inputManifest;
      const filesForCurrentDs =
        manifest?.filesByDataSource?.[formData.dataSource ?? ''];

      if (formData.dataSource && !filesForCurrentDs) {
        formData.dataSource = '';
        formData.s3InputKey = '';
        inputGridApi.value?.deselectAll();
      } else if (
        formData.s3InputKey &&
        !(filesForCurrentDs ?? []).includes(formData.s3InputKey)
      ) {
        formData.s3InputKey = '';
        inputGridApi.value?.deselectAll();
      }
    } finally {
      isRefreshingManifest.value = false;
    }
  };

  const onInputSelectionChanged = (event: SelectionChangedEvent) => {
    const rows = event.api.getSelectedRows() as InputFileRow[];
    formData.s3InputKey = rows[0]?.key ?? '';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file: File): boolean => {
    fileError.value = '';

    if (!file.name.toLowerCase().endsWith('.zip')) {
      fileError.value = t('invalidFileType');
      return false;
    }

    const validMimeTypes = [
      'application/zip',
      'application/x-zip-compressed',
      'application/x-zip',
    ];
    if (file.type && !validMimeTypes.includes(file.type)) {
      fileError.value = t('invalidFileType');
      return false;
    }

    return true;
  };

  const handleFileChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (file) {
      if (validateFile(file)) {
        selectedFile.value = file;
      } else {
        selectedFile.value = null;
        if (fileInput.value) {
          fileInput.value.value = '';
        }
      }
    } else {
      selectedFile.value = null;
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile.value || !formData.systemName) {
      alertStore.showNotification(
        t('pleaseFillRequired'),
        t('batchUpload'),
        Motif.ERROR
      );
      return;
    }

    isSubmitting.value = true;
    showProgressModal.value = true;
    progressMessage.value = '';
    progressPercent.value = 0;

    try {
      const metadata: BatchMetadata = {
        systemName: formData.systemName,
      };

      if (formData.s3InputKey) metadata.s3InputKey = formData.s3InputKey;
      if (formData.dataSource) metadata.dataSource = formData.dataSource;

      const response = await batchStore.streamUploadMetadata(
        {
          deploymentFile: selectedFile.value,
          metadata,
        },
        (event: BatchMetadataProgressEvent) => {
          progressMessage.value = event.message;
          if (event.total > 0) {
            progressPercent.value = Math.round(
              (event.current / event.total) * 100
            );
          }
        }
      );
      progressPercent.value = 100;

      alertStore.showNotification(
        t('uploadSuccessful', { s3Key: response.s3Key }),
        t('batchUpload'),
        Motif.SUCCESS
      );

      selectedFile.value = null;
      if (fileInput.value) {
        fileInput.value.value = '';
      }
      formData.systemName = '';
      formData.s3InputKey = '';
      formData.dataSource = '';
      inputGridApi.value?.deselectAll();

      emit('success');
    } catch (error: any) {
      console.error('Upload failed:', error);
      alertStore.showNotification(
        error?.message || error?.response?.data?.message || t('uploadFailed'),
        t('batchUpload'),
        Motif.ERROR
      );
    } finally {
      isSubmitting.value = false;
      showProgressModal.value = false;
      progressMessage.value = '';
      progressPercent.value = 0;
    }
  };

  const handleCancel = () => {
    emit('cancel');
  };
</script>

<style scoped>
  .batch-metadata-form {
    max-width: 800px;
    margin: 0 auto;
  }
</style>
